import { DatePipe } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ViewEncapsulation,
  Inject,
  OnDestroy,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, EMPTY, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import {
  ApiInsightResponse,
  AttributeValues,
  DrivingCondition,
  EVRangeData,
  InsightPayload,
  ModelOutput,
  OptimizationTip,
} from 'src/app/model';
import { GeneralService, ServicecallService } from 'src/app/services';
import { StatusCard } from '../trip-segment-popover/trip-segment-popover.component';

interface DataSourceConfig {
  useApiData: boolean;
  useDummyInsights: boolean;
  debugMode: boolean;
}

@Component({
  selector: 'app-optimization-summary',
  templateUrl: './optimization-summary.component.html',
  styleUrl: './optimization-summary.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class OptimizationSummaryComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() feedback = new EventEmitter<boolean>();

  private readonly destroy$ = new Subject<void>();
  popOverData: EVRangeData = this.initializeDefaultData();
  options: any;
  tripHeartbeatAttributes: AttributeValues[] = [];
  private _tenantConfig: any;
  tripOptimizationData : ModelOutput;
  currentTripScore = 0.0;
  maxScore = 10;
  lastTripChange : string ;
  efficiencyGain : number;
  scoreDiff: number;
  currentTripPosition : 5;
  totalEnergyUsed : number;
  maxRange : string;
  currentTripIncluded : boolean = false;

  //Trip performance
  trips :any[] = [];

  //Attribute details (from config)
  statusData: StatusCard[] = [
    { id: 1, value: null, label: 'Harsh Brakes', type: 'brake', attribute: "Harsh Brakes" },
    { id: 2, value: null, label: 'Harsh Acceleration', type: 'brake', attribute: "Harsh Acceleration" },
    { id: 3, value: null, label: 'Overspeed', type: 'brake', attribute: "Overspeed" },
    { id: 4, value: null, label: 'Idle time', type: 'brake', attribute: "Idle time" },
    { id: 5, value: null, label: 'Battery Temp', type: 'temperature', unit: '°C', attribute: "battery_temp" },
    { id: 6, value: null, label: 'Motor Temp', type: 'temperature', unit: '°C' , attribute: "motor_temp"},
    { id: 7, value: null, label: 'Inverter Temp', type: 'temperature', unit: '°C' , attribute: "inverter_temp"},
    { id: 8, value: null, label: 'Ambient Temp', type: 'temperature', unit: '°C' , attribute: "ambient_temp"},
  ];

  optimizationTipsAttributes : {attrName: string, name: string, icon?: string}[] = [
    {attrName: "Speed", name: "speed", icon : null},
    {attrName: "Acceleration", name: "acceleration", icon : null},
    {attrName: "HVAC", name: "hvac", icon : null},
  ]

  // Configuration for data source switching
  private dataConfig: DataSourceConfig = {
    useApiData: true, // Set to false to use dummy data
    useDummyInsights: false, // Set to false when API is ready
    debugMode: false, // Set to false in production
  };
  linearProgressOption: any;

  //Subscriptions
  private _getTripDetailsSubscription: Subscription;
  private _trackEntitySubscription: Subscription;
  private _tripSegmentSubscription: Subscription;
  private _tripPerformanceSubscription: Subscription;
  private _tripScoreSubscription : Subscription;
  private _attributeAggregateSubscription : Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _apiService: ServicecallService,
    private _datePipe: DatePipe,
    private _generalService: GeneralService
  ) {}

  ngOnInit(): void {
    this.statusData = this._apiService.getConfiguration('tenantGenericConfig')?.prescriptiveInsights?.statusData ?? [];
    this.optimizationTipsAttributes = this._apiService.getConfiguration('tenantGenericConfig')?.prescriptiveInsights?.optimizationTipsAttributes ?? [];
    this.loadInsightData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._getTripDetailsSubscription?.unsubscribe();
    this._trackEntitySubscription?.unsubscribe();
    this._tripSegmentSubscription?.unsubscribe();
    this._tripPerformanceSubscription?.unsubscribe();
    this._tripScoreSubscription?.unsubscribe();
    this._attributeAggregateSubscription?.unsubscribe();
  }

  onClose() {
    this.close.emit();
  }

  onFeedback(isHelpful: boolean) {
    this.feedback.emit(isHelpful);
    this._apiService.toasterService({
      type: 'success',
      msg: 'Feedback submitted successfully',
    });
  }

  formatValue(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString();
    }
    return value;
  }

  getSaving(name: string): string {
    const savings = this.popOverData?.optimizationTips?.find(
      (item: OptimizationTip) => item.attributeName === name
    )?.outcome;
    return savings || 'N/A';
  }
  /**
   * Main method to load insight data based on configuration
   */
  private loadInsightData(): void {
    if (this.dataConfig.debugMode) {
      console.log('Data Source Config:', this.dataConfig);
    }
    if (this.dataConfig.useApiData && !this.dataConfig.useDummyInsights) {
      this._getInsightFromApi();
    } else if (this.dataConfig.useDummyInsights) {
      this.loadDummyInsightData();
    } else {
      // Fallback to existing data structure
      this.popOverData = this.initializeDefaultData();
    }
  }
  /**
   * Load dummy insight data for development/demo
   */
  private loadDummyInsightData(): void {
    if (this.dataConfig.debugMode) {
      console.log('Loading dummy insight data...');
    }
    // Simulate API delay for realistic testing
    setTimeout(() => {
      this.popOverData = {
        ...this.popOverData, // Keep existing tripInfo
        maximumRange: 262,
        optimizationTips: this.getDummyOptimizationTips(),
        drivingConditions: this.getDummyDrivingConditions(),
      };

      if (this.dataConfig.debugMode) {
        console.log('Dummy data loaded:', this.popOverData);
      }
    }, 500); // Simulate network delay
  }
  /**
   * Get dummy optimization tips
   */
  private getDummyOptimizationTips(): OptimizationTip[] {
    return [
      {
        attributeName: '',
        text: 'Optimization Tips',
        outcome: '+ 71 km',
      },
      {
        attributeName: 'speed',
        text: 'Reduce speed to 65 km/h',
        outcome: '+ 45 km',
      },
      {
        attributeName: 'acceleration',
        text: 'Smoother acceleration',
        outcome: '+ 12 km',
      },
      {
        attributeName: 'hvac',
        text: 'Lower HVAC to 0.5kW',
        outcome: '+ 14 km',
      },
    ];
  }
  /**
   * Get dummy driving conditions
   */
  private getDummyDrivingConditions(): DrivingCondition[] {
    return [
      {
        attributeName: 'battery',
        actualValue: '70 %',
        prescribedValue: '80 %',
      },
      {
        attributeName: 'speed',
        actualValue: '75 kmph',
        prescribedValue: '65 kmph',
        currentImage:
          '../../../../../../../assets/images/current-value-dummy.png',
      },
      {
        attributeName: 'acceleration',
        actualValue: '0.4 m/s2',
        prescribedValue: '0.3 m/s2',
        currentImage: '../../../../../../../assets/images/acc-img-dummy.png',
      },
      {
        attributeName: 'hvac',
        actualValue: '1.8 kW',
        prescribedValue: '0.5 kW',
        currentImage:
          '../../../../../../../assets/images/current-value-dummy.png',
      },
      {
        attributeName: 'temperature',
        actualValue: '16 °C',
        prescribedValue: '20 °C',
        currentImage:
          '../../../../../../../assets/images/current-value-dummy.png',
      },
    ];
  }
  /**
   * Fetch insight data from API
   */
  private _getInsightFromApi(): void {
    if (this.dataConfig.debugMode) {
      console.log('Fetching data from API...');
    }
    this.getTripHeartbeatData();
    this.getAttributeAggregate();
    this.loadTripOptimizationData();
    this.getTripPerformanceScore();
    // const payload: InsightPayload = {
    //   class: 'SOH',
    //   entityId: '679a6fdfbfe96c51653f57a6d',
    //   tripId: null,
    // };
    // this._apiService
    //   .getInsight(payload)
    //   .pipe(takeUntil(this.destroy$), catchError(this.handleError.bind(this)))
    //   .subscribe({
    //     next: (result: ApiInsightResponse[]) => {
    //       if (this.isValidResponse(result)) {
    //         this.mapApiResponseToComponent(result[0]);
    //       } else {
    //         // Fallback to dummy data if API fails
    //         if (this.dataConfig.debugMode) {
    //           console.log('API validation failed, falling back to dummy data');
    //         }
    //         this.loadDummyInsightData();
    //       }
    //     },
    //     error: () => {
    //       // Fallback to dummy data on error
    //       if (this.dataConfig.debugMode) {
    //         console.log('API error, falling back to dummy data');
    //       }
    //       this.loadDummyInsightData();
    //     },
    //   });
  }
  /**
   * Validates API response format
   */
  private isValidResponse(result: any): result is ApiInsightResponse[] {
    if (!Array.isArray(result) || result.length === 0) {
      this._apiService.toasterService({
        type: 'warning',
        msg: 'Invalid response format or empty data',
      });
      return false;
    }
    const firstItem = result[0];
    if (!firstItem?.modelOutput) {
      this._apiService.toasterService({
        type: 'warning',
        msg: 'Missing model output in response',
      });
      return false;
    }
    return true;
  }
  /**
   * Maps API response to component data structure
   */
  private mapApiResponseToComponent(apiData: ApiInsightResponse): void {
    try {
      const { modelOutput } = apiData;
      this.popOverData = {
        ...this.popOverData, // Keep existing tripInfo
        maximumRange: this.parseNumericValue(
          modelOutput.calculations.maxRange,
          262
        ),
        optimizationTips: this.mapOptimizationTips(
          modelOutput.optimizationTips,
          modelOutput.calculations.rangeImprovement
        ),
        drivingConditions: this.mapDrivingConditions(
          modelOutput.drivingConditions
        ),
      };
      if (this.dataConfig.debugMode) {
        console.log('Successfully mapped API data:', this.popOverData);
      }
    } catch (error) {
      console.error('Error mapping API response:', error);
      this._apiService.toasterService({
        type: 'error',
        msg: 'Error processing insight data',
      });
      // Fallback to dummy data on mapping error
      this.loadDummyInsightData();
    }
  }
  /**
   * Maps optimization tips from API response
   */
  private mapOptimizationTips(
    apiTips: any[],
    rangeImprovement: string
  ): OptimizationTip[] {
    const tips: OptimizationTip[] = [
      {
        attributeName: '',
        text: 'Optimization Tips',
        outcome: rangeImprovement || '+ 71 km',
      },
    ];
    if (apiTips && Array.isArray(apiTips)) {
      const mappedTips = apiTips.map((tip) => ({
        attributeName: tip.attributeName || '',
        text: tip.text || '',
        outcome: tip.outcome || 'N/A',
      }));
      tips.push(...mappedTips);
    }
    return tips;
  }
  /**
   * Maps driving conditions from API response
   */
  private mapDrivingConditions(apiConditions: any[]): DrivingCondition[] {
    if (!apiConditions || !Array.isArray(apiConditions)) {
      return this.getDefaultDrivingConditions();
    }
    const imageMap: Record<string, string> = {
      speed: '../../../../../../../assets/images/current-value-dummy.png',
      acceleration: '../../../../../../../assets/images/acc-img-dummy.png',
      hvac: '../../../../../../../assets/images/current-value-dummy.png',
      temperature: '../../../../../../../assets/images/current-value-dummy.png',
      battery: '../../../../../../../assets/images/current-value-dummy.png',
    };
    return apiConditions.map((condition) => ({
      attributeName: condition.attributeName || '',
      actualValue: condition.actualValue || 'N/A',
      prescribedValue: condition.prescribedValue || '',
      currentImage:
        imageMap[condition.attributeName.toLowerCase()] || undefined,
    }));
  }
  /**
   * Parses numeric values from API strings
   */
  private parseNumericValue(value: string, defaultValue: number): number {
    if (!value) return defaultValue;
    const parsed = parseFloat(value.replace(/[^\d.]/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  }
  /**
   * Error handler for API calls
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Failed to fetch insight data';
    if (error?.status) {
      errorMessage += ` (Status: ${error.status})`;
    }

    if (this.dataConfig.debugMode) {
      console.error('API Error:', error);
    }

    this._apiService.toasterService({
      type: 'error',
      msg: errorMessage,
    });

    return EMPTY;
  }
  /**
   * Initialize default data structure (keeps tripInfo intact)
   */
  private initializeDefaultData(): EVRangeData {
    return {
      tripInfo: {
        tripId: this.data?.tile1?.tripId ?? 'NA',
        vehicleId: this.data?.tile1?.entityName ?? 'NA',
        startLocation: this.data?.tile1?.startLocation ?? 'NA',
        endLocation: this.data?.tile1?.endLocation ?? 'NA',
        startTime: this.data?.tile1?.tripStartDate ?? 'NA',
        endTime: this.data?.tile1?.tripEndDate ?? 'NA',
        distance: this.calculateDistance(this.data?.tile1?.distanceTravelled),
        duration: this.data?.tile1?.timeTaken ?? 0,
        avgSpeed: this.calculateSpeed(this.data?.tile1?.avgSpeed),
        maxSpeed: this.calculateSpeed(this.data?.tile1?.topSpeed),
        remainingRange: this.data?.tile1?.possibleRange ?? 191,
        tags: this.data?.tile1?.tags,
        imageUrl: this.data?.tile1?.imageUrl,
      },
      maximumRange: this.data?.tile1?.maxRange ?? 262,
      optimizationTips: [],
      drivingConditions: [],
    };
  }
  /**
   * Helper method to calculate distance
   */
  private calculateDistance(distance: number | undefined): string | number {
    if (distance == null) return 'NA';
    return (distance)?.toFixed(2);
  }
  /**
   * Helper method to calculate speed
   */
  private calculateSpeed(speed: number | undefined): string | number {
    if (speed == null) return 'NA';
    if (speed === 0) return 0;
    return speed.toFixed(2);
  }
  /**
   * Default driving conditions fallback
   */
  private getDefaultDrivingConditions(): DrivingCondition[] {
    return [
      {
        attributeName: 'battery',
        actualValue: '70%',
        prescribedValue: '',
      },
      {
        attributeName: 'speed',
        actualValue: '75 kmph',
        prescribedValue: '65 kmph',
        currentImage:
          '../../../../../../../assets/images/current-value-dummy.png',
      },
      {
        attributeName: 'acceleration',
        actualValue: '0.4 m/s',
        prescribedValue: '0.3 m/s',
        currentImage: '../../../../../../../assets/images/acc-img-dummy.png',
      },
      {
        attributeName: 'HVAC',
        actualValue: '1.8 kW',
        prescribedValue: '0.5 kW',
        currentImage:
          '../../../../../../../assets/images/current-value-dummy.png',
      },
    ];
  }

  // ===== DEVELOPMENT/TESTING UTILITIES =====
  /**
   * Method to switch data source during runtime (for testing)
   * Call this from browser console: component.switchDataSource()
   */
  public switchDataSource(): void {
    this.dataConfig.useDummyInsights = !this.dataConfig.useDummyInsights;
    console.log(
      `Switched to ${this.dataConfig.useDummyInsights ? 'DUMMY' : 'API'} data`
    );
    this.loadInsightData();
  }

  /**
   * Method to toggle debug mode
   */
  public toggleDebugMode(): void {
    this.dataConfig.debugMode = !this.dataConfig.debugMode;
    console.log(`Debug mode: ${this.dataConfig.debugMode ? 'ON' : 'OFF'}`);
  }

  /**
   * Method to get current data source info
   */
  public getDataSourceInfo(): void {
    console.log('Current Data Source Configuration:', this.dataConfig);
    console.log('Current Data:', this.popOverData);
  }

  /**
   * Get category name for optimization tips
   */
  getCategoryName(tipText: string, index: number): string {
    if (!tipText) {
      const defaultCategories = [
        'Eco-Driving',
        'Regenerative Braking',
        'Thermal Management',
        'Route Optimization',
      ];
      return defaultCategories[index] || 'Optimization';
    }

    // Extract category based on tip content
    const text = tipText.toLowerCase();

    if (
      text.includes('drive') ||
      text.includes('acceleration') ||
      text.includes('speed')
    ) {
      return 'Eco-Driving';
    } else if (text.includes('brake') || text.includes('regenerative')) {
      return 'Regenerative Braking';
    } else if (
      text.includes('thermal') ||
      text.includes('temperature') ||
      text.includes('cooling')
    ) {
      return 'Thermal Management';
    } else if (text.includes('route') || text.includes('path')) {
      return 'Route Optimization';
    } else if (text.includes('battery') || text.includes('charge')) {
      return 'Battery Management';
    } else {
      return 'General';
    }
  }

  /**
   * Check if popover data is valid
   */
  isDataValid(): boolean {
    return !!(this.popOverData && this.popOverData.tripInfo);
  }

  /**
   * Get thermal status class
   */
  getThermalStatusClass(temp: number): string {
    if (temp < 30) return 'good';
    if (temp < 60) return 'warning';
    return 'danger';
  }

  /**
   * Get performance metric class
   */
  getMetricClass(value: number): string {
    if (value >= 8.5) return 'good';
    if (value >= 7.0) return 'average';
    return 'poor';
  }

  /**
   * Calculate performance score percentage for progress ring
   */
  getPerformanceScorePercentage(): number {
    const score = 8.2; // You can get this from popOverData
    return (score / 10) * 100;
  }

  /**
   * Get stroke dash offset for progress ring
   */
  getStrokeDashOffset(): number {
    const circumference = 2 * Math.PI * 50; // radius = 50
    const percentage = this.getPerformanceScorePercentage();
    return circumference - (percentage / 100) * circumference;
  }

  get progressPercentage(): number {
    return (this.currentTripScore / this.maxScore) * 100;
  }

  get strokeDasharray(): string {
    const circumference = 2 * Math.PI * 45; // radius = 45
    return `${
      (this.progressPercentage / 100) * circumference
    } ${circumference}`;
  }

  /**
   * Sets donut chart data for Trip Performance
   * @param maxScore
   * @param currentScore
   */
  setDonutChartData(maxScore: number, currentScore: number) {
    this.options = {
      series: {
        name: 'Trip Performance',
        type: 'pie',
        radius: ['80%', '95%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        roundCap: true,
        label: {
          show: false,
          position: 'center',
        },
        tooltip: {
          show: false,
        },
        labelLine: {
          show: false,
        },
        data: currentScore === 0 ? 
        [{
          value: currentScore,
          name: 'Current Score',
          itemStyle: { color: 'rgba(229, 231, 235, 1)', borderRadius: 10 },
        }] : 
        [
          {
            value: currentScore,
            name: 'Current Score',
            itemStyle: { color: 'rgba(0, 210, 255, 1)', borderRadius: 10 },
          },
          {
            value: maxScore - currentScore,
            name: 'Remaining Score',
            itemStyle: { color: 'rgba(229, 231, 235, 1)' },
          },
        ],
      },
      legend: {
        selectedMode: false,
        show: false,
      },
      graphic: {
        type: 'group',
        left: 'center',
        top: 'center',
        children: [
          {
            type: 'text',
            left: 'center',
            top: -10,
            style: {
              text: currentScore,
              fontFamily: 'Inter',
              fontSize: 26,
              fontWeight: 'bold',
              fill: 'rgba(14, 192, 230, 1)',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: 20,
            style: {
              text: 'out of 10',
              fontFamily: 'Inter',
              fontSize: 8.71,
              fontWeight: 'normal',
              fill: 'rgba(156, 163, 175, 1)',
            },
          },
        ],
      },
      grid: {
        top: '0%',
        left: '0%',
        right: '0%',
        bottom: '0%',
      },
    };
  }

  trackByFn(index: number, item: StatusCard): number {
    return item.id;
  }

   /**
  * Gets trip heartbeat data
  */
    getTripHeartbeatData() {
     const payload = {
       siteId: this._apiService.getSiteId(),
       entityId: this.data?.tile1?.entityId,
       to :  this._datePipe.transform(this.data?.tile1?.endTime, 'yyyy-MM-ddTHH:mm:ssZZ')
     };
     this._trackEntitySubscription = this._apiService.trackEntity(payload, 1).subscribe({
       next: (result: any) => {
         if (result['status']) {
           if (result['response']['data']?.length) {
            const tableData = [...(result?.response?.data ?? [])].find((item: any) => Object.keys(item ?? {})?.includes('original'));
            const originalAttributes = tableData?.original?.[0]?.attributes;
            this.tripHeartbeatAttributes = Array.isArray(originalAttributes) ? originalAttributes : [];
            if (Array.isArray(this.statusData)) {
              this.statusData?.forEach((statusItem) => {
                const matchedAttribute = this.tripHeartbeatAttributes?.find(
                  (attr) => attr?.attributeName?.trim()?.toLowerCase() === statusItem?.attribute?.trim()?.toLowerCase()
                );
                if(matchedAttribute) {
                  statusItem.value = Number(matchedAttribute?.attributeValue)?.toFixed(1) ?? null;
                }
              });
              this.getAlertsCount();
            }
           } else {
             this.tripHeartbeatAttributes = [];
             this.getAlertsCount();
           }
         }
         else {
           this.tripHeartbeatAttributes = [];
           this.getAlertsCount();
         }
       },
       error: (error) => {
         this.tripHeartbeatAttributes = [];
         this.getAlertsCount();
         this._apiService?.toasterService({ type: 'error', msg: error?.message })
       }
     });
   }
  
    /**
     * Gets attribute value
     * @param attrName 
     * @returns  
     */
    getAttributeValue(attrName: string) {
      return this.tripHeartbeatAttributes?.find(item => item.attributeName === attrName)?.attributeValue;
    }
  
  
    /**
     * Gets alerts count
     */
    getAlertsCount() {
      const reqData = {
        from: this._datePipe.transform(this.data?.tile1?.startTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
        to: this._datePipe.transform(this.data?.tile1?.endTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
        siteId: this._apiService.getSiteId(),
        filterBy: "Alert",
        status: true,
        classType: "Alert",
        entityId: this.data?.tile1?.entityId
      };
      this._apiService.getAlertsCount(reqData, false).subscribe((result: any) => {
        if (result['status']) {
          const alert = result['response'] ? result['response']['data']?.find((item: any) => item.alert)?.alert : null;
          this.statusData?.forEach((statusItem)=> {
            const matchedAlert = alert?.find(item => item.alert === statusItem.attribute);
            if(matchedAlert) {
              statusItem.value = matchedAlert?.count;
            }
          })
        }
        else {
          this._apiService.toasterService({ type: 'error', msg: result?.response?.message });
        }
      })
    }

    /**
   * Loads trip optimization insights data
   */
  private loadTripOptimizationData(): void {
    let configStartDate = null;
    let configEndDate = null;
    this._tenantConfig = this._apiService?.getConfiguration('tenantGenericConfig') ? this._apiService?.getConfiguration('tenantGenericConfig') : null;

    if (this._tenantConfig?.defaultTimeRange) {
      const configHour = this._tenantConfig?.defaultTimeRange || 0;

      const { startDate, endDate } = this._apiService.calculateDateRange(configHour);
      configEndDate = endDate;
      configStartDate = startDate;
    }
    let startTime;
    let endTime;
    if(this._tenantConfig?.tripHistoryProfilingBasedOn === 'tripId') {
      if(this.data?.tile1?.selectedDate){
        startTime = this.data?.tile1?.selectedDate?.['start'];
        endTime =  this.data?.tile1?.selectedDate?.['end'];
      } else {
        [startTime,endTime] = this._generalService?.getStartEndTimeByDate();
      }
    } else {
      [startTime, endTime] =  this.data?.tile1?.selectedDate ? this._generalService?.getStartEndTimeForSelectedDate( this.data?.tile1?.selectedDate, this._tenantConfig?.tenant, this._tenantConfig?.defaultTimeRange) : this._generalService?.getStartEndTimeByDate();
    }
    const reqPayload = {
      entityId: this.data?.tile1?.entityId,
      // from: this._datePipe.transform(this.data?.tile1?.aggregateDate?.from ?? ((!this.data?.tile1?.selectedDate && configStartDate) ? configStartDate : startTime), 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(this.data?.tile1?.aggregateDate?.to ?? ((!this.data?.tile1?.selectedDate && configEndDate) ? configEndDate : endTime), 'yyyy-MM-ddTHH:mm:ssZZ'),
      limit: 1,
      type: "PrescriptiveInsights_Trip",
      ...(this.data?.tile1?.tripId && {tripId: this.data?.tile1?.tripId})
    }
    this._tripSegmentSubscription = this._apiService.getTripSegments(reqPayload).subscribe({
      next: (result: ApiInsightResponse[]) => {
        this.tripOptimizationData = result?.[0]?.modelOutput;
        if(this.tripOptimizationData) {
          this.tripOptimizationData.calculations.possibleRange = Number(this.tripOptimizationData.calculations.possibleRange)?.toFixed(2);
          this.maxRange = Number(this.tripOptimizationData?.calculations?.maxRange)?.toFixed(2);
          this.setDonutChartData(this.maxScore, this.tripOptimizationData?.summary?.score);
          this.calculateTripPerformance(result?.[0]?.dataFrom);
          this.getCurrentTripScore();
        }
      }, error: () => {
        this.tripOptimizationData = null;
      }
    })
  }

  /**
   * Gets trip performance score
   */
  getTripPerformanceScore() {
    const reqPayload = {
      entityId: this.data?.tile1?.entityId,
      type: "PrescriptiveInsights_Trip",
      count: 3
    }
    this._tripPerformanceSubscription = this._apiService.getTripPerformaceScore(reqPayload).subscribe({
      next: (result: any) => {
        this.trips = [...result];
        if(this.trips?.some(item => item.tripId === this.data?.tile1?.tripId)) {
          this.currentTripIncluded = true;
        } else {
          this.currentTripIncluded = false;
        }
      }, error: () => {
        this.trips = [];
      }
    })
  }

  // selectTripPerformace(index : number) {
  //   this.trips?.forEach((element,i) => {
  //     if(i === index) {
  //       element.isCurrent = true;
  //       this.setDonutChartData(this.maxScore, this.trips[i]?.modelOutput?.summary?.score);
  //       this.calculateTripPerformance(this.trips?.[i]?.dataTo);
  //     } else {
  //       element.isCurrent = false;
  //     }
  //   })
  // }

  /**
   * Calculates trip performance
   * @param fromDate 
   */
  calculateTripPerformance(fromDate: string) {
    const prevTripToDate = new Date(new Date(fromDate).getTime() - 1000); //Subtracting 1sec ,100ms = 1sec

    const reqPayload: any = {
      entityId: this.data?.tile1?.entityId,
      to: this._datePipe.transform(prevTripToDate, 'yyyy-MM-ddTHH:mm:ssZZ'),
      type: "PrescriptiveInsights_Trip",
      limit: 1,
    }
    this._tripSegmentSubscription = this._apiService.getTripSegments(reqPayload).subscribe({
      next: (result: any) => {
        if(result?.length) {
          this.scoreDiff = Number((this.tripOptimizationData?.summary?.score - result?.[0]?.modelOutput?.summary?.score)?.toFixed(2));
          const effGain = Number(((Number(this.tripOptimizationData?.calculations?.rangeImprovement) / Number(result?.[0]?.modelOutput?.calculations?.rangeImprovement)) *100)?.toFixed(2));
          this.efficiencyGain = isFinite(effGain) ? effGain : null ;
        }
      }, error: () => {
        this.scoreDiff = null;
        this.efficiencyGain = null;
      }
    })
  }

  /**
   * Gets current trip score
   */
  getCurrentTripScore() {
    const reqPayload: any = {
      entityId: this.data?.tile1?.entityId,
      ...(this.data?.tile1?.tripId && {tripId: this.data?.tile1?.tripId})
    }
    this._tripScoreSubscription = this._apiService.getCurrentTripPosition(reqPayload).subscribe({
      next: (result: any) => {
        this.currentTripPosition = result?.rank ?? 5;
      }, error: () => {
        this.currentTripPosition = 5;
      }
    })
  }

  /**
   * Gets attribute aggregate
   */
  getAttributeAggregate() {
    const reqPayload = {
      from: this._datePipe.transform(this.data?.tile1?.startTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(this.data?.tile1?.endTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
      attributeNames: ["total_energy_consumed"],
      operation: "sum",
      entityId: this.data?.tile1?.entityId
    }
    this._attributeAggregateSubscription = this._apiService.getAttributeAggregate(reqPayload).subscribe({
      next: (result: any) => {
        this.totalEnergyUsed = Number((result?.[0]?.aggregateValue || 0).toFixed(2));
      }, error: () => {
        this.totalEnergyUsed = null;
      }
    })
  }

    /**
   * Gets optimization attributes name
   * @param attr 
   * @returns  
   */
     getOptimizationAttributesName(attr: string) {
      return this.optimizationTipsAttributes?.find(item => item.name === attr)?.attrName;
    }

  /**
   * Gets range improvement display
   * @returns range improvement display 
   */
  getRangeImprovementDisplay(): string {
    const improvement = this.tripOptimizationData?.calculations?.rangeImprovement;
    if (improvement == null) {
      return '--';
    }
    const value = Number(Number(improvement)?.toFixed(2));
    const sign = value >= 0 ? '+' : '';
    return `${sign}${Math.abs(value)}km`;
  }

  /**
   * Formats trip id
   * @param tripId 
   * @returns trip id 
   */
  formatTripId(tripId: string): string {
    if (!tripId) return '--';
    const start = tripId.slice(0, 3);
    const end = tripId.slice(-3);
    return `${start}...${end}`;
  }

  roundOfftoDecmal(value: string) {
    return Number(value)?.toFixed(2);
  }
}
